import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import { movies } from "@/mocks/movies";
import type { Movie, MovieType } from "@/types/movie";
import { filterMovies, paginate } from "@/utils/movieFilters";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;

type TypeFilter = "all" | MovieType;

const typeOptions: { key: TypeFilter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";
  const initialType = (searchParams.get("type") as TypeFilter) ?? "all";

  const [input, setInput] = useState(initialQ);
  const [applied, setApplied] = useState(initialQ);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(initialType);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  usePageMeta({
    title: applied ? `搜索“${applied}” - 91在线看` : "影视搜索 - 91在线看",
    description: "在91在线看搜索电影、电视剧资源，支持片名、演员、类型模糊匹配，快速找到想看的内容。",
    keywords: "91在线看,影视搜索,电影搜索,电视剧搜索,在线观看",
  });

  useEffect(() => {
    const q = searchParams.get("q") ?? "";
    const t = (searchParams.get("type") as TypeFilter) ?? "all";
    setInput((prev) => (prev.trim() === q ? prev : q));
    setApplied(q);
    setTypeFilter(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (input.trim() === applied) return;
    setLoading(true);
    const timer = setTimeout(() => {
      const value = input.trim();
      setApplied(value);
      setLoading(false);
      setPage(1);
      const next = new URLSearchParams();
      if (value) next.set("q", value);
      if (typeFilter !== "all") next.set("type", typeFilter);
      setSearchParams(next, { replace: true });
    }, 500);
    return () => clearTimeout(timer);
  }, [input, applied, typeFilter, setSearchParams]);

  const allMovies = movies as Movie[];

  const results = useMemo(
    () =>
      applied
        ? filterMovies(allMovies, {
            keyword: applied,
            type: typeFilter === "all" ? undefined : typeFilter,
            sort: "latest",
          })
        : [],
    [allMovies, applied, typeFilter]
  );

  const { items, total, totalPages, page: safePage } = useMemo(
    () => paginate(results, page, PAGE_SIZE),
    [results, page]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleTypeChange = (key: TypeFilter) => {
    setTypeFilter(key);
    setPage(1);
    const next = new URLSearchParams();
    if (applied) next.set("q", applied);
    if (key !== "all") next.set("type", key);
    setSearchParams(next, { replace: true });
  };

  const handleReset = () => {
    setInput("");
    setApplied("");
    setTypeFilter("all");
    setPage(1);
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const hasResults = results.length > 0;

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
      <div className="pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />
      </div>

      <header className="pt-5 md:pt-7">
        <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
          影视搜索
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600">
          {applied ? (
            <>
              关键词 <span className="text-accent-400 font-semibold">“{applied}”</span> 的搜索结果
            </>
          ) : (
            "输入片名、演员或类型关键词，快速定位你想看的影视内容。"
          )}
        </p>
      </header>

      {/* 搜索框 */}
      <form onSubmit={handleSubmit} className="mt-6 md:mt-8">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center bg-background-100 border border-background-200 rounded-md pl-4 pr-2 h-12 focus-within:border-primary-500 transition-colors">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="text"
              name="q"
              placeholder="搜索影视名称、演员、类型…"
              className="flex-1 bg-transparent text-base text-foreground-950 placeholder:text-foreground-500 px-3 outline-none"
            />
            {input && (
              <button
                type="button"
                onClick={() => setInput("")}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-900 cursor-pointer"
                aria-label="清空"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="h-12 px-8 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm md:text-base font-label whitespace-nowrap transition-colors cursor-pointer"
          >
            搜索
          </button>
        </div>
      </form>

      {/* 类型筛选 + 统计 */}
      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
          {typeOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => handleTypeChange(opt.key)}
              className={`h-8 px-4 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                typeFilter === opt.key
                  ? "bg-secondary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {applied && (
          <p className="text-xs md:text-sm text-foreground-500">
            共找到 <span className="text-accent-400 font-semibold">{total}</span> 条结果
          </p>
        )}
      </div>

      {/* 结果 */}
      {loading ? (
        <div className="mt-10 md:mt-16 flex flex-col items-center justify-center py-16 text-center">
          <span className="w-12 h-12 rounded-full border-2 border-background-300 border-t-accent-500 animate-spin"></span>
          <p className="mt-5 text-sm text-foreground-500">正在为你搜索…</p>
        </div>
      ) : !applied ? (
        <div className="mt-10 md:mt-16 rounded-lg bg-background-100 border border-dashed border-background-300 py-16 md:py-20 flex flex-col items-center text-center px-6">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-500">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h2 className="mt-5 font-heading text-lg md:text-xl font-bold text-foreground-900">
            开始搜索你喜欢的影视
          </h2>
          <p className="mt-2 text-sm text-foreground-500 max-w-md">
            试试输入“科幻”“刑侦”或演员名字，海量资源一搜即达。
          </p>
        </div>
      ) : hasResults ? (
        <>
          <div className="mt-6 md:mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-7 md:gap-x-5">
            {items.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-10 md:mt-16 rounded-lg bg-background-100 border border-dashed border-background-300 py-16 md:py-20 flex flex-col items-center text-center px-6">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h2 className="mt-5 font-heading text-lg md:text-xl font-bold text-foreground-900">
            未找到相关影视资源
          </h2>
          <p className="mt-2 text-sm text-foreground-500 max-w-md">
            换个关键词试试，或清空条件回到首页看看热门推荐。
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap justify-center">
            <Link
              to="/"
              className="h-10 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-label flex items-center whitespace-nowrap transition-colors cursor-pointer"
            >
              返回首页
            </Link>
            <button
              type="button"
              onClick={handleReset}
              className="h-10 px-6 rounded-md border border-background-300 text-foreground-700 hover:bg-background-200 text-sm font-label whitespace-nowrap transition-colors cursor-pointer"
            >
              重置搜索
            </button>
          </div>
        </div>
      )}
    </div>
  );
}